import { styled } from '@mui/material/styles';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { keyframes } from '@mui/system';
import { useBanner } from '../contexts/BannerContext';

const fadeScaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeScaleOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(1.05);
  }
`;

const BannerContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  direction: 'rtl',
  overflow: 'hidden',
  minHeight: '48px', // Ensure consistent height during transitions
}));

const BannerContent = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  margin: 0,
  padding: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
  },
  '&': {
    textAlign: 'center !important',
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(1),
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const MessageContainer = styled(Box)<{ isExiting: boolean }>(({ isExiting }) => ({
  position: 'absolute',
  width: '100%',
  animation: `${isExiting ? fadeScaleOut : fadeScaleIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
  animationFillMode: 'forwards',
}));

interface TopBannerProps {
  onClose?: () => void;
  showCloseButton?: boolean;
}

export const TopBanner: React.FC<TopBannerProps> = ({
  onClose,
  showCloseButton = true,
}) => {
  const { bannerTexts } = useBanner();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (bannerTexts.length <= 1) return;

    const interval = setInterval(() => {
      setIsExiting(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % bannerTexts.length);
        setIsExiting(false);
      }, 500); // Match the animation duration
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [bannerTexts.length]);

  if (bannerTexts.length === 0) return null;

  const currentBanner = bannerTexts[currentIndex];

  return (
    <BannerContainer>
      <MessageContainer isExiting={isExiting}>
        <BannerContent variant="body1">
          {currentBanner.text}
        </BannerContent>
      </MessageContainer>
      {showCloseButton && (
        <CloseButton onClick={onClose} size="small">
          <CloseIcon />
        </CloseButton>
      )}
    </BannerContainer>
  );
}; 